import { useMemo, useCallback, useState, lazy, Suspense } from 'react';
import { Menu, Tooltip } from 'antd';
import {
  SearchOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { useHistory, useLocation } from 'react-router-dom';
import { useTourRef } from 'packages/omnitag/hooks/use-tour-refs';
import { isStagingEnv } from 'packages/env';
import { Document, ArrowUpRight } from '@carbon/icons-react';

const styles = {
  menu: css`
    border-right: 0;
    flex-grow: 1;
    height: 100%;
  `,
};

/**
 * A map of keys to lazily-loaded components.
 * Using React.lazy allows us to split the code for each page into a separate chunk,
 * which is only loaded when needed.
 *
 * IMPORTANT: For pre-rendering to work, the router must use these exact same
 * `React.lazy` instances.
 */
const componentMap = {
  tools: lazy(() => import('./my-tools')),
  search: lazy(() => import('../tool/root')),
  'data-requests': lazy(() => import('./data-requests')),
};

// Helper to determine the correct docs URL based on the environment
const getDocsURL = (): string => {
  if (isStagingEnv) {
    return 'https://polaris-docs.ml-stage.motional.com';
  }
  return 'https://polaris-docs.ml.motional.com';
};

export function PolarisSider() {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  /**
   * State to keep track of which component keys have been hovered over.
   * This drives the pre-rendering mechanism.
   */
  const [componentsToPreRender, setComponentsToPreRender] = useState<string[]>(
    []
  );
  const menuRef = useTourRef<HTMLDivElement>('side-navigation-menu');

  const currentView = useMemo(() => {
    if (pathname.includes('/data-requests')) {
      return 'data-requests';
    }
    if (pathname.includes('/tools')) {
      return 'tools';
    }
    if (pathname.includes('/polaris')) {
      return 'search';
    }
    return 'tools';
  }, [pathname]);

  /**
   * Callback triggered on mouse enter for a menu item.
   * It adds the item's key to the `componentsToPreRender` state, which
   * in turn causes the hidden component to be rendered, thus pre-loading and
   * pre-rendering it.
   */
  const preloadAndRender = useCallback(
    (key: string) => {
      if (!componentsToPreRender.includes(key)) {
        setComponentsToPreRender((prev) => [...prev, key]);
      }
    },
    [componentsToPreRender]
  );

  /**
   * Memoized navigation items for the Ant Design Menu.
   * Each item has an `onMouseEnter` handler to trigger the pre-rendering
   * of the associated page component.
   */
  const navigationItems = useMemo(() => {
    return [
      {
        key: 'tools',
        label: (
          <Tooltip title="Access various Polaris tools" placement="right">
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              Tools
            </span>
          </Tooltip>
        ),
        icon: <AppstoreOutlined />,
        onMouseEnter: () => preloadAndRender('tools'),
      },
      {
        key: 'search',
        label: (
          <Tooltip title="Search for data" placement="right">
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              Search
            </span>
          </Tooltip>
        ),
        icon: <SearchOutlined />,
        onMouseEnter: () => preloadAndRender('search'),
      },
      {
        key: 'data-requests',
        label: (
          <Tooltip title="View and manage your data requests" placement="right">
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              Data Requests
            </span>
          </Tooltip>
        ),
        icon: <DatabaseOutlined />,
        onMouseEnter: () => preloadAndRender('data-requests'),
      },
      {
        key: 'docs-external',
        label: (
          <Tooltip title="View Polaris documentation" placement="right">
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              Docs{' '}
              <ArrowUpRight style={{ width: '0.85rem', marginLeft: '0.5em' }} />
            </span>
          </Tooltip>
        ),
        icon: <Document style={{ width: '0.75rem' }} />,
        onClick: () => window.open(getDocsURL(), '_blank'),
      },
    ];
  }, [preloadAndRender]);

  const handleMenuItemClick = useCallback(
    ({ key }: { key: string }) => {
      const basePath = location.pathname.includes('/mobis') ? '/mobis' : '';
      switch (key) {
        case 'tools':
          history.push(`${basePath}/polaris/tools`);
          break;
        case 'search':
          history.push(`${basePath}/polaris`);
          break;
        case 'data-requests':
          history.push(`${basePath}/polaris/data-requests`);
          break;
        case 'docs-external':
          // Handled by onClick in navigationItems for external link
          break;
      }
    },
    [history, location.pathname]
  );

  return (
    <div ref={menuRef}>
      <Menu
        selectedKeys={[currentView]}
        mode="inline"
        items={navigationItems}
        onClick={handleMenuItemClick}
        className={styles.menu}
      />
      {/*
        This hidden div is the core of the pre-rendering strategy.
        When a user hovers over a menu item, the item's key is added to `componentsToPreRender`.
        React then renders the corresponding lazy component inside this hidden div.

        - The `Suspense` boundary with a `null` fallback prevents any loading indicators from appearing.
        - Rendering the component (even if hidden) triggers the dynamic `import()` inside `React.lazy`.
        - The browser downloads the component's code.
        - React proceeds to render the component, running its initial hooks and any data fetching it might do on mount.

        By the time the user clicks, the component is often fully loaded and ready to be displayed instantly.
      */}
      <div style={{ display: 'none', visibility: 'hidden' }}>
        <Suspense fallback={null}>
          {componentsToPreRender.map((key) => {
            const Component = componentMap[key as keyof typeof componentMap];
            return Component ? <Component key={key} /> : null;
          })}
        </Suspense>
      </div>
    </div>
  );
}
